// ═══════════════════════════════════════════════
// Asana API — Elixir Prep (via Netlify proxy)
// ═══════════════════════════════════════════════

const CONFIG = {
  projects: {
    PREP_PHARMACIES: '1211623729050940',
    INCIDENTS_PREP: '1211623105481531',
    RECLAM_PHARMACIES: '1211747027211280',
  },
  sections: {
    EN_PREPARATION: '1211623802139564',
    TERMINEE_OK: '1211623802139566',
  },
  fields: {
    DATE_PREPARATION: '1211597687257421',   // type: date
    PREPARATEUR: '1211954635514244',         // type: people
    ANOMALIE: '1211623105481519',            // type: multi_enum
    CIP_CONCERNE: '1211623105481528',        // type: text
    COMMENTAIRE_ANOMALIE: '1211476910652264',// type: text
  },
  anomalyEnums: {
    manquant: '1211623105481520',
    produit_abime: '1211623105481523',
    ecart_plus: '1211623105481522',
    ecart_moins: '1211671090017718',
    peremption: '1211623105481524',
    mauvais_lot: '1211671481247222',
    erreur_ref: '1211623105481525',
    autre: '1211671090017719',
  },
};

async function asanaCall(action, params) {
  const res = await fetch('/asana-api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ action, params }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    console.error('Asana API error ' + res.status + ':', text);
    throw new Error('Asana ' + res.status);
  }
  return (await res.json()).result;
}

async function createTask({ projectGid, name, notes, customFields, memberships }) {
  const result = await asanaCall('createTask', { projectGid, name, notes, customFields, memberships });
  return result?.data || null;
}

async function updateTask(taskGid, data) {
  const result = await asanaCall('updateTask', { taskGid, data });
  return result?.data || null;
}

async function getTask(taskGid) {
  const result = await asanaCall('getTask', { taskGid });
  return result?.data || null;
}

// ── Créer tâche pharmacie ──
export async function createPrepTask(session, preparateurAsanaGid) {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  const dayMonth = String(today.getDate()).padStart(2, '0') + '/' +
    String(today.getMonth() + 1).padStart(2, '0');

  // Custom fields — formats corrects par type
  const customFields = {};

  // Date → objet {date: "YYYY-MM-DD"}
  customFields[CONFIG.fields.DATE_PREPARATION] = { date: dateStr };

  // People → array de GIDs
  if (preparateurAsanaGid) {
    customFields[CONFIG.fields.PREPARATEUR] = [preparateurAsanaGid];
  }

  const task = await createTask({
    projectGid: CONFIG.projects.PREP_PHARMACIES,
    name: session.partner_name + ' ' + dayMonth,
    customFields,
    notes: 'Commandes: ' + (session.order_refs || []).join(', ') +
      '\nAdresse: ' + (session.adresse || '') +
      '\nTournée: ' + (session.tournee || '') +
      '\n\nCréé depuis Elixir Prep',
  });

  console.log('Asana task created:', task?.gid, task?.name);
  return task?.gid || null;
}

// ── Déplacer vers section ──
export async function moveToSection(taskGid, sectionGid) {
  await asanaCall('moveToSection', { sectionGid, taskGid });
}

// ── Ajouter commentaire ──
export async function addComment(taskGid, text) {
  await asanaCall('addComment', { taskGid, text });
}

// ── Upload photo as Asana attachment ──
export async function uploadAttachment(taskGid, photoUrl, filename) {
  try {
    await asanaCall('uploadAttachment', { taskGid, photoUrl, filename });
    return true;
  } catch (e) {
    console.warn('Attachment upload failed:', e);
    return false;
  }
}

// ── MAJ custom fields anomalie ──
export async function updateAnomalyFields(taskGid, anomalies) {
  if (!anomalies || !anomalies.length) return;

  // Multi-enum → array de GIDs
  const enumGids = anomalies
    .map(a => CONFIG.anomalyEnums[a.anomaly_type])
    .filter(Boolean);

  const cips = anomalies
    .map(a => a.product_name || '')
    .filter(Boolean)
    .join(', ');

  const comments = anomalies
    .map(a => (a.product_name || '?') + ' → ' + a.anomaly_type)
    .join('\n');

  const customFields = {};
  if (enumGids.length) customFields[CONFIG.fields.ANOMALIE] = enumGids;
  if (cips) customFields[CONFIG.fields.CIP_CONCERNE] = cips;
  if (comments) customFields[CONFIG.fields.COMMENTAIRE_ANOMALIE] = comments;

  await updateTask(taskGid, { custom_fields: customFields });
}

// ── Sync complète session → Asana ──
export async function syncSessionToAsana(session, user, caisses, anomalies, remarks, photos, nbCartons, nbCartonsVrac) {
  let taskGid = session.asana_task_gid;

  // 1) Créer tâche si pas encore faite
  if (!taskGid) {
    taskGid = await createPrepTask(session, user.asana_gid);
  }

  // 2) Mettre à jour le nom et la description avec caisses/cartons
  const ambCount = caisses ? caisses.filter(c => c.type === 'ambiant').length : 0;
  const froidCount = caisses ? caisses.filter(c => c.type === 'froid').length : 0;
  const nStd = nbCartons || 0;
  const nVrac = nbCartonsVrac || 0;
  const totalColis = ambCount + froidCount + nStd + nVrac;

  // Construire le résumé colis pour le nom
  const colisParts = [];
  if (ambCount > 0) colisParts.push(ambCount + ' amb');
  if (froidCount > 0) colisParts.push(froidCount + ' froid');
  if (nStd > 0) colisParts.push(nStd + ' std');
  if (nVrac > 0) colisParts.push(nVrac + ' vrac');
  const colisStr = colisParts.length > 0 ? ' — ' + colisParts.join(' · ') : '';

  const dayMonth = String(new Date().getDate()).padStart(2, '0') + '/' +
    String(new Date().getMonth() + 1).padStart(2, '0');

  // Codes des caisses
  const codes = caisses ? caisses.map(c => c.code + ' (' + c.type + ')').join(', ') : '';

  // Anomalies résumé
  const anomSummary = anomalies && anomalies.length > 0
    ? '\n\nAnomalies (' + anomalies.length + '):\n' + anomalies.map(a =>
      '⚠️ ' + (a.product_name || '?') + ' — ' + a.anomaly_type + (a.product_included === false ? ' (NON MIS)' : a.product_included === true ? ' (mis)' : '')
    ).join('\n')
    : '';

  // Remarques résumé
  const remSummary = remarks && remarks.length > 0
    ? '\n\nRemarques:\n' + remarks.map(r => '• ' + r.text).join('\n')
    : '';

  // Préparateurs identifiés
  const preparateurs = [];
  if (session.prepared_by_name) preparateurs.push(session.prepared_by_name);
  if (user.name && !preparateurs.includes(user.name)) preparateurs.push(user.name);
  const prepStr = preparateurs.length > 0 ? ' [' + preparateurs.join(', ') + ']' : '';

  // Mettre à jour nom + description
  await updateTask(taskGid, {
    name: session.partner_name + ' ' + dayMonth + colisStr + prepStr,
    notes: 'Commandes: ' + (session.order_refs || []).join(', ') +
      '\nAdresse: ' + (session.adresse || '') +
      '\nTournée: ' + (session.tournee || '') +
      '\nPréparateur(s): ' + preparateurs.join(', ') +
      '\n\nColis: ' + (colisParts.join(' + ') || 'aucun') +
      (codes ? '\nDétail caisses: ' + codes : '') +
      anomSummary + remSummary +
      '\n\nMis à jour depuis Elixir Prep',
  });

  // 3) Commentaire caisses + cartons
  if (totalColis > 0) {
    let txt = user.name + ' — ' + colisParts.join(' + ');
    if (codes) txt += '\n' + codes;
    await addComment(taskGid, txt);
  }

  // 3) Anomalies → custom fields + commentaire
  if (anomalies && anomalies.length > 0) {
    await updateAnomalyFields(taskGid, anomalies);
    const txt = 'Anomalies (' + user.name + '):\n' +
      anomalies.map(a => '⚠️ ' + (a.product_name || '?') + ' — ' + a.anomaly_type).join('\n');
    await addComment(taskGid, txt);
  }

  // 4) Remarques → commentaire
  if (remarks && remarks.length > 0) {
    await addComment(taskGid,
      'Remarques (' + user.name + '):\n' + remarks.map(r => '• ' + r.text).join('\n')
    );
  }

  // 5) Photos → upload en pièces jointes Asana (miniatures cliquables)
  if (photos && photos.length > 0) {
    for (let i = 0; i < photos.length; i++) {
      try {
        // Préférer storage_path (le serveur résout en signed URL fraîche)
        const photoRef = photos[i].storage_path || photos[i].url;
        await uploadAttachment(taskGid, photoRef, 'photo-' + (i + 1) + '.jpg');
      } catch (e) { console.warn('Prep photo attachment ' + (i + 1) + ' failed:', e); }
    }
  }

  return taskGid;
}

// ═══ RÉCLAMATIONS ═══

const RECLAM_FIELDS = {
  NOM_PHARMACIE: '1211747031099067',
  CODE_POSTAL: '1211747027751525',
  NUM_COMMANDE: '1211747030901821',
  TYPE_RECLAMATION: '1211747027751527',
  URGENCE: '1211785642961858',
  NOM_PRODUIT: '1211747192268749',
  CIP_13: '1211747027751537',
  QTY: '1211747027751539',
  DATE_RECLAMATION: '1211746798106481',
  STATUT_INTERNE: '1211747089310041',
};

const RECLAM_TYPE_MAP = {
  produit_manquant: '1211747027751530',  // Problème produit
  produit_abime: '1211747027751530',
  dlu_depassee: '1211747027751530',
  erreur_commande: '1211747027751529',   // Autre problème commande
  avoir: '1211747027751531',             // Demande avoir
  facture: '1211747027751528',           // Problème facture
  autre: '1211747027751533',             // Autre motif
};

const RECLAM_URGENCE_MAP = {
  normale: '1211785642961861',
  urgente: '1211785642961862',
};

// Fetch open réclamations from Asana
export async function fetchAsanaReclamations() {
  try {
    const allTasks = [];
    let offset = null;
    const optFields = 'name,completed,notes,custom_fields,created_at,permalink_url';

    // Paginate through all Asana results
    do {
      const json = await asanaCall('listProjectTasks', {
        projectGid: CONFIG.projects.RECLAM_PHARMACIES,
        completedSince: '2026-01-01T00:00:00Z',
        optFields,
        limit: 100,
        offset,
      });
      const tasks = json?.data;
      if (!tasks || !Array.isArray(tasks)) break;
      allTasks.push(...tasks);
      offset = json.next_page?.offset || null;
    } while (offset);

    return allTasks.filter(t => !t.completed).map(t => {
      const cf = {};
      (t.custom_fields || []).forEach(f => { cf[f.gid] = f; });
      return {
        asana_gid: t.gid,
        name: t.name,
        partner_name: cf[RECLAM_FIELDS.NOM_PHARMACIE]?.text_value || '',
        code_postal: cf[RECLAM_FIELDS.CODE_POSTAL]?.number_value || '',
        order_ref: cf[RECLAM_FIELDS.NUM_COMMANDE]?.text_value || '',
        product_name: cf[RECLAM_FIELDS.NOM_PRODUIT]?.text_value || '',
        cip: cf[RECLAM_FIELDS.CIP_13]?.text_value || '',
        qty: cf[RECLAM_FIELDS.QTY]?.number_value || 1,
        urgence: cf[RECLAM_FIELDS.URGENCE]?.display_value === 'Urgente' ? 'urgente' : 'normale',
        type_display: cf[RECLAM_FIELDS.TYPE_RECLAMATION]?.display_value || '',
        statut_display: cf[RECLAM_FIELDS.STATUT_INTERNE]?.display_value || '',
        description: t.notes || '',
        created_at: t.created_at,
        permalink: t.permalink_url,
      };
    });
  } catch (e) {
    console.error('Fetch Asana réclamations error:', e);
    return [];
  }
}

// Create réclamation task in Asana
export async function createAsanaReclamation(claim) {
  const today = new Date().toISOString().split('T')[0];
  const dayMonth = String(new Date().getDate()).padStart(2, '0') + '/' +
    String(new Date().getMonth() + 1).padStart(2, '0');

  const customFields = {};
  customFields[RECLAM_FIELDS.DATE_RECLAMATION] = { date: today };
  if (claim.partner_name) customFields[RECLAM_FIELDS.NOM_PHARMACIE] = claim.partner_name;
  if (claim.order_ref) customFields[RECLAM_FIELDS.NUM_COMMANDE] = claim.order_ref;
  if (claim.product_name) customFields[RECLAM_FIELDS.NOM_PRODUIT] = claim.product_name;
  if (claim.cip) customFields[RECLAM_FIELDS.CIP_13] = claim.cip;
  if (claim.qty) customFields[RECLAM_FIELDS.QTY] = claim.qty;
  if (RECLAM_URGENCE_MAP[claim.urgence]) customFields[RECLAM_FIELDS.URGENCE] = RECLAM_URGENCE_MAP[claim.urgence];
  if (RECLAM_TYPE_MAP[claim.type]) customFields[RECLAM_FIELDS.TYPE_RECLAMATION] = [RECLAM_TYPE_MAP[claim.type]];

  const task = await createTask({
    projectGid: CONFIG.projects.RECLAM_PHARMACIES,
    name: (claim.partner_name || 'Réclamation') + ' ' + dayMonth + ' — ' + (claim.product_name || claim.type),
    customFields,
    notes: 'Pharmacie: ' + (claim.partner_name || '') +
      '\nProduit: ' + (claim.product_name || '') +
      '\nCommande: ' + (claim.order_ref || '') +
      '\nType: ' + (claim.type || '') +
      '\n\n' + (claim.description || '') +
      '\n\n———————————————\nCréé depuis Elixir Prep App',
  });

  return task?.gid || null;
}

// ═══ RÉCEPTION TRANSPORTEURS ═══

const RECEPTION_PROJECT = '1211623727846186';
const RECEPTION_SECTIONS = {
  VALIDATION_CHAUFFEUR: '1211624232674281',
  ANOMALIE: '1211624232655428',
  VERIF_PRODUIT: '1211624144553073',
  SUIVI_COLIS: '1211624692335167',
  VALIDEE: '1211623727846187',
};

// ─── Résolution utilisateur Prep → compte Asana (par nom, cache) ───
let _asanaUsersCache = null;
export async function resolveAsanaUserGid(prepName) {
  if (!prepName) return null;
  try {
    if (!_asanaUsersCache) {
      const res = await asanaCall('getWorkspaceUsers', {});
      _asanaUsersCache = res?.data || [];
    }
    const norm = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const tokens = norm(prepName).split(/\s+/).filter(t => t.length >= 3);
    if (!tokens.length) return null;
    // 1. Tous les tokens du nom Prep présents dans le nom Asana
    const strict = _asanaUsersCache.filter(u => tokens.every(t => norm(u.name).includes(t)));
    if (strict.length === 1) return strict[0].gid;
    // 2. Sinon : premier token (prénom) trouvé chez un seul utilisateur
    const byFirst = _asanaUsersCache.filter(u => norm(u.name).includes(tokens[0]));
    if (byFirst.length === 1) return byFirst[0].gid;
    return null; // ambigu → on laisse vide plutôt que de se tromper
  } catch { return null; }
}

export async function createAsanaReception(data) {
  const date = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const dateISO = new Date().toISOString().split('T')[0];
  const anomLabel = data.anomalie ? '⚠️ ANOMALIE' : '✅ OK';
  const totalColis = (data.nb_ambiant || 0) + (data.nb_froid || 0);
  const taskName = (data.numero_qualite ? data.numero_qualite + ' — ' : '')
    + data.laboratoire_name + ' — ' + data.transporteur + ' — ' + date + ' — ' + anomLabel;

  const notes = [
    'Laboratoire : ' + data.laboratoire_name,
    'Transporteur : ' + data.transporteur,
    (data.nb_ambiant || 0) > 0 ? 'Colis ambiants : ' + data.nb_ambiant : '',
    (data.nb_froid || 0) > 0 ? 'Colis froids : ' + data.nb_froid : '',
    data.temperature ? '🌡️ Température relevée : ' + data.temperature + '°C' + (parseFloat(data.temperature) > 8 ? ' ⚠️ ALERTE' : '') : '',
    (data.nb_palettes || 0) > 0 ? 'Palettes : ' + data.nb_palettes : '',
    'Anomalie : ' + (data.anomalie ? 'OUI — ' + (data.anomalie_types || []).join(', ') : 'Non'),
    data.anomalie_detail ? 'Détail : ' + data.anomalie_detail : '',
    '',
    'Réceptionné par : ' + (data.user_name || '?'),
    'Date : ' + date,
    '',
    '———————————————',
    'Créé depuis Elixir Prep App',
  ].filter(Boolean).join('\n');

  // État des colis enum
  let etatColis = '1211623727846200'; // Bon
  if (data.anomalie_types?.some(t => ['cartons_abimes', 'produit_abime'].includes(t))) etatColis = '1211623727846201'; // Abîmé
  if (data.anomalie_types?.some(t => ['colis_manquant', 'cartons_manquants', 'produit_manquant'].includes(t))) etatColis = '1211623727846202'; // Manquant

  // Custom fields
  const custom_fields = {
    '1211597531550142': { date: dateISO },             // Date de réception (format Asana: objet {date})
    '1211597695159826': data.transporteur,             // Nom du transporteur
    '1213147986440531': Number(data.nb_palettes) || 0, // Nombre de palettes
    '1211623727846194': Number(totalColis) || 0,       // Nombre de colis reçus
    '1211597695159822': Number(data.nb_ambiant) || 0,  // Nb colis ambiants
    '1211597695159824': Number(data.nb_froid) || 0,    // Nb colis froids
    '1211623727846199': etatColis,                      // État des colis
    '1211476910652264': data.anomalie_detail || '',    // Commentaire anomalie
  };

  // Réceptionnaire réel (compte Asana résolu par nom) — sinon la règle
  // 'Auto' met toujours le propriétaire du jeton API (Sam)
  const receptGid = await resolveAsanaUserGid(data.user_name);
  if (receptGid) custom_fields['1211623727846192'] = [receptGid];

  // Conformité au bon émargé du transporteur : Non si colis/cartons manquants
  const manquants = data.anomalie_types?.some(t => ['colis_manquant', 'cartons_manquants'].includes(t));
  custom_fields['1211611394404316'] = manquants ? '1211611394404320' : '1211611394404319'; // Non / Oui

  // Température (uniquement si colis froids) : relevé (text) + conformité 2-8°C
  if ((data.nb_froid || 0) > 0 && data.temperature !== null && data.temperature !== undefined && data.temperature !== '') {
    custom_fields['1211597696774811'] = String(data.temperature); // Relevé de la Température
    const t = parseFloat(String(data.temperature).replace(',', '.'));
    if (!isNaN(t)) {
      custom_fields['1211608773788455'] = (t >= 2 && t <= 8) ? '1211608773788456' : '1211608773788457'; // T° Conforme OUI / NON
    }
  }

  // Anomalies multi-enum (dédupliquées — colis_manquant et cartons_manquants
  // pointent sur la même option car "Colis manquant" est désactivée dans Asana)
  if (data.anomalie_asana_gids?.length > 0) {
    custom_fields['1211596423886160'] = [...new Set(data.anomalie_asana_gids)];
  }

  // Create task
  // Anomalie → tâche assignée à la cheffe d'équipe (notification Asana
  // native sur son téléphone) avec échéance AUJOURD'HUI : le P doit être
  // renseigné dans la journée
  let assignee = null;
  if (data.anomalie) {
    assignee = await resolveAsanaUserGid('Majda');
  }

  const task = await createTask({
    projectGid: RECEPTION_PROJECT,
    assignee: assignee || undefined,
    dueOn: data.anomalie ? dateISO : undefined,
    name: taskName,
    notes,
    customFields: custom_fields,
  });

  if (!task?.gid) return null;

  // Move to correct section (memberships in create doesn't always work)
  const section = data.anomalie ? RECEPTION_SECTIONS.ANOMALIE : RECEPTION_SECTIONS.VALIDEE;
  await moveToSection(task.gid, section);

  // Upload photos as real attachments (miniatures cliquables)
  if (data.photo_urls?.length > 0) {
    for (let i = 0; i < data.photo_urls.length; i++) {
      try {
        await uploadAttachment(task.gid, data.photo_urls[i], 'photo-' + (i + 1) + '.jpg');
      } catch (e) { console.warn('Photo ' + (i + 1) + ' attachment failed:', e); }
    }
  }

  return task.gid;
}

export { CONFIG };
